import { CSSProperties, useState } from 'react'
import ButtonSymphony from 'elements/ButtonSymphony'
import Colors from 'modules/Colors'
import { ReactComponent as InstagramIcon } from 'assets/images/instagram-bordered-icon.svg'
import { ReactComponent as FacebookRoundedIcon } from 'assets/images/facebook-rounded-icon.svg'
import { ReactComponent as InstagramBwIcon } from 'assets/images/instagram-bw.svg'
import { clsx, styx } from 'modules/Utils'
import Sty from 'modules/Sty'
import Icon from 'elements/Icon'
import StepSymphony from 'elements/StepSymphony'
import useScreen from 'Hooks/useScreen'

export interface ConnectionStepProps {
    /** Main title for the step */
    stepTitle: string
    /** Title of the connection */
    connectedAccountName?: string
    /** Icon for the connection */
    connectedAccountIcon?: string | JSX.Element
    /** Description text explaining the step */
    stepDescription?: string
    /** Additional content to render below the connection details */
    children?: React.ReactNode
    /** Whether the connection is completed */
    isCompleted?: boolean
    /** Icon element to display */
    stepIcon?: JSX.Element | string
    /** Background color for the icon */
    iconContainerColor?: string
    /** Optional thumbnail image URL */
    thumbnail?: string
    /** Array of additional descriptive texts */
    connectedAccountDescriptions?: string[]
    /** Whether the step can be edited when completed */
    canEdit?: boolean
    /** Tooltip text for the edit button */
    tooltip?: string
    /** Whether the connect/edit button should be disabled */
    disabledButton?: boolean
    /** Error message to display */
    errorText?: string
    /** Callback for button click */
    onButtonClick?: () => void
    /** Whether to show Instagram icon */
    instagramIcon?: boolean
    /** Whether to show Facebook icon */
    fbIcon?: boolean
    /** Instagram handle that's linked */
    igLinked?: string
    /** Whether to show social media icons in subtitle */
    showSubtextIcon?: boolean
    /** Loading state for async operations */
    isLoading?: boolean
    /** Custom button text */
    buttonText?: string
    /** Custom connected text */
    connectedText?: string,
    /** Optional right side content to display */
    rightSideContent?: React.ReactNode
    /** Whether the step is the last step */
    isLastStep?: boolean
    /** Whether to hide the line indicator */
    hideLineIndicator?: boolean
}

const ConnectionStep = ({
    stepTitle,
    stepDescription,
    connectedAccountName,
    connectedAccountIcon,
    isCompleted,
    stepIcon,
    connectedAccountDescriptions,
    canEdit = true,
    tooltip,
    disabledButton,
    errorText,
    onButtonClick,
    instagramIcon,
    fbIcon,
    igLinked,
    showSubtextIcon = true,
    isLoading,
    buttonText,
    connectedText,
    rightSideContent,
    isLastStep = false,
    hideLineIndicator = false
}: ConnectionStepProps) => {
    const { mobileView } = useScreen();

    const renderSocialIcon = () => {
        if (connectedAccountIcon && typeof connectedAccountIcon !== 'string') {
            return connectedAccountIcon
        } else if (connectedAccountIcon && typeof connectedAccountIcon === 'string') {
            return <Icon className="expressSetupStepIcon" icon={connectedAccountIcon} size={40} />
        }

        if (instagramIcon) {
            return (
                <div style={sty.connectionIcon}>
                    <InstagramBwIcon />
                </div>
            )
        }

        if (fbIcon) {
            return (
                <div style={sty.connectionIcon}>
                    <FacebookRoundedIcon />
                </div>
            )
        }

        if (stepIcon && typeof stepIcon === 'string') {
            return (
                <Icon className="expressSetupStepIcon" icon={stepIcon} size={40} />
            )
        } else if (stepIcon) {
            return stepIcon
        }

        return null
    }

    const renderSocialSubtext = () => {
        if (!showSubtextIcon || !isCompleted) {
            return null
        }

        if (instagramIcon) {
            return (
                <div className="instagramSubtext flex gap-1 items-center">
                    <InstagramIcon width={16} height={16} />
                    <div style={Sty.text12grey}>IG Page</div>
                </div>
            )
        }

        if (fbIcon) {
            return (
                <div className="flex gap-1 items-center">
                    <FacebookRoundedIcon width={16} height={16} />
                    <div style={Sty.text12grey}>FB Page</div>
                </div>
            )
        }

        return ''
    }

    const renderLinkedAccount = () => {
        if (!igLinked) {
            return null
        }

        return (
            <div className="flex items-center gap-1">
                <div style={Sty.text12}>Linked to: </div>
                <Icon className="instagramIcon" icon="instagram" size={12} />
                <div style={Sty.text12grey}>@{igLinked}</div>
            </div>
        )
    }

    const renderConnectOrEditButton = () => {
        if (!onButtonClick) {
            return null
        }

        if (isCompleted) {
            return (
                <>
                    <ButtonSymphony
                        className="1"
                        variant="text"
                        onClick={onButtonClick}
                        disabled={!canEdit}
                        tooltip={tooltip}
                        size={mobileView ? 'small' : 'medium'}
                    >
                        {buttonText || 'Edit'}
                    </ButtonSymphony>
                    <ButtonSymphony
                        className="editStepButtonConnected"
                        color="connected"
                        size={mobileView ? 'small' : 'medium'}
                    >
                        {connectedText || 'Connected'}
                    </ButtonSymphony>
                </>
            )
        }

        return (
            <ButtonSymphony
                className="buttonConnectStepDetail"
                variant="outlined"
                onClick={onButtonClick}
                minWidth={109}
                disabled={disabledButton || isLoading}
                isLoading={isLoading}
            >
                {buttonText || 'Connect'}
            </ButtonSymphony>
        )
    }

    const renderConnectionDetails = () => {
        return (
            <div
                className="connectionStepMainContainer"
                style={sty.connectionStepMainContainer}
            >
                <div className="connectionStepDescriptionContainer" style={sty.descriptionContainer}>
                    {renderSocialIcon()}
                    <div className="connectionStepDescription flex flex-col gap-1">
                        {/* {renderSocialSubtext()} */}
                        <div className="connectionStepDescriptionTitle">
                            {connectedAccountName || stepTitle}
                        </div>
                        {connectedAccountDescriptions?.map((desc, index) => (
                            <div key={index} className="connectionStepDescriptionSubtext" style={Sty.text14grey}>
                                {desc}
                            </div>
                        ))}
                        {renderLinkedAccount()}
                        {errorText &&
                            <div className="connectionStepDescriptionErrorText" style={{ ...Sty.text12grey, color: Colors.yellow }}>
                                {errorText}
                            </div>
                        }
                    </div>
                </div>
                <div className="connectionStepRightSideContainer flex gap-4 items-center">
                    {rightSideContent}
                    {renderConnectOrEditButton()}
                </div>
            </div>
        )
    }

    // NOTE: render()
    return (
        <StepSymphony
            hideLineIndicator={hideLineIndicator}
            className="connectionStepComponent"
            style={sty.connectionStepComponent}
            title={stepTitle}
            description={stepDescription}
            isLastStep={isLastStep}
        >
            {renderConnectionDetails()}
        </StepSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    connectionStepComponent: {

    },
    contentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    connectionStepMainContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 12,
        backgroundColor: Colors.white,
        borderRadius: 8,
        border: Sty.border,
        gap: 16,
    },
    descriptionContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%'
    },
    thumbnail: {
        minWidth: 40,
        minHeight: 40,
        maxWidth: 40,
        maxHeight: 40,
        borderRadius: 8,
        objectFit: 'cover'
    },
    greenContainer: {
        display: 'flex',
        gap: 8,
        height: 39,
        padding: '12px 16px',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 48,
        backgroundColor: Colors.PAPER_SUCCESS_CONTAINER
    },
    connectionIcon: {
        minHeight: 40,
        minWidth: 40,
        maxHeight: 40,
        maxWidth: 40,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
        backgroundColor: Colors.PAPER_LIGHT_GRAY_CONTAINER,
    },
    activeConnectionIcon: {
        minHeight: 40,
        minWidth: 40,
        maxHeight: 40,
        maxWidth: 40,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8
    }
}

export default ConnectionStep